import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { productsAPI, cartAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { addToLocalCart, notifyCartChanged } from '../lib/localCart';
import { ArrowLeft, ArrowRight, ShoppingCart, Weight, Ruler, Package, Minus, Plus, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import Seo from '../components/Seo';
import ProgressiveImage from '../components/ProgressiveImage';
import { useMagnetic } from '../hooks/useMagnetic';

import { productImage, resolveSrc } from '../lib/productImage';
import { SECTORS } from '../data/sectors';
import { guiasParaProducto } from '../lib/relatedGuides';
import ProductCard from '../components/ProductCard';
import FaqSection from '../components/FaqSection';
import { productFaq, faqSchema, medidasTexto } from '../lib/productFaq';
import { AcceptedLogos } from '../components/PaymentMethods';

// Google corta el snippet de búsqueda (y WhatsApp/Facebook la vista previa
// del link, que reutiliza esta misma description como og:description) a
// partir de ~160 caracteres. La descripción que carga el admin es de
// longitud libre, así que se recorta en el último espacio ANTES del límite
// —nunca a media palabra— para dejar sitio siempre al precio y al envío,
// que es el motivo por el que ese sufijo se agrega en primer lugar: se
// perdía justo ahí en 13 de 13 productos.
function metaDescription(body, suffix, max = 155) {
    const budget = max - suffix.length;
    if (body.length <= budget) return `${body}${suffix}`;
    const cut = body.slice(0, Math.max(0, budget - 1));
    const lastSpace = cut.lastIndexOf(' ');
    const trimmed = (lastSpace > 0 ? cut.slice(0, lastSpace) : cut).trimEnd();
    return `${trimmed}…${suffix}`;
}

export default function ProductDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [quantity, setQuantity] = useState(1);
    const [selectedSize, setSelectedSize] = useState('');
    const [adding, setAdding] = useState(false);
    const [added, setAdded] = useState(false);
    const [selectedVariantImg, setSelectedVariantImg] = useState(null);
    // Índice dentro de [image_url, ...photos] — las hasta 3 fotos del MISMO
    // producto (otro ángulo/toma), distintas de gallery_images (diseños
    // intercambiables). Elegir una foto limpia la variante elegida y
    // viceversa: son dos selectores independientes sobre la misma foto hero.
    const [selectedPhotoIdx, setSelectedPhotoIdx] = useState(0);
    // Para el cross-sell de abajo: se pide el catálogo completo UNA vez (no
    // por producto) porque cualquier ficha a la que llegue el visitante va a
    // necesitarlo. Si falla, el cross-sell simplemente no aparece — no vale
    // la pena mandar al visitante al catálogo por un error en un bloque
    // secundario de la página.
    const [allProducts, setAllProducts] = useState([]);
    const addToCartRef = useMagnetic(0.25, 8);
    // Barra de compra fija (solo celular): el botón "Agregar" de la ficha
    // queda debajo de la foto, la descripción y las especificaciones, así que
    // en un teléfono hay que bajar bastante para encontrarlo. Cuando ese
    // bloque no está a la vista, la barra mantiene el total y el botón al
    // alcance del pulgar.
    // Ref por callback + estado: el bloque se monta/desmonta con el
    // esqueleto de carga, y así el observador siempre apunta al nodo vigente.
    const [buyEl, setBuyEl] = useState(null);
    const [stickyBuy, setStickyBuy] = useState(false);

    useEffect(() => {
        productsAPI.getOne(id).then(res => {
            setProduct(res.data);
            if (res.data.sizes?.length) setSelectedSize(res.data.sizes[0]);
            setLoading(false);
        }).catch(() => { setLoading(false); navigate('/catalogo'); });
    }, [id, navigate]);

    useEffect(() => {
        productsAPI.getAll().then(res => setAllProducts(res.data)).catch(() => {});
    }, []);

    useEffect(() => {
        if (!buyEl || typeof IntersectionObserver === 'undefined') return undefined;
        const io = new IntersectionObserver(([entry]) => setStickyBuy(!entry.isIntersecting), { threshold: 0 });
        io.observe(buyEl);
        return () => io.disconnect();
    }, [buyEl]);

    const handleAddToCart = async () => {
        setAdding(true);
        try {
            if (user) {
                await cartAPI.add({ product_id: product.id, quantity, selected_size: selectedSize });
                notifyCartChanged();
            } else {
                // Sin cuenta el carrito vive en el navegador hasta el checkout.
                // Antes esto mandaba a /auth sin aviso: quien hacía clic en
                // "Agregar" caía en una pantalla de login sin entender por qué.
                addToLocalCart(product.id, quantity, selectedSize);
            }
            setAdded(true);
            toast.success('Producto agregado al carrito', {
                action: { label: 'Ver carrito', onClick: () => navigate('/carrito') },
            });
            setTimeout(() => setAdded(false), 2000);
        } catch (err) {
            toast.error('Error al agregar al carrito');
        }
        setAdding(false);
    };

    // El esqueleto ocupa la altura de la pantalla, no 384 px.
    //
    // Esta página está PRE-RENDERIZADA: el HTML llega con la ficha completa.
    // Pero al hidratar, `loading` arranca en true y React reemplaza todo eso
    // por el esqueleto; cuando llega el fetch, vuelve a poner la ficha. Con
    // un esqueleto bajito el pie de página subía hasta el hueco y luego caía
    // otra vez — medido: 0.313 de CLS, con el footer como responsable casi
    // exclusivo. Reservando el alto del viewport, el footer se queda fuera de
    // pantalla en los dos estados y el salto deja de existir.
    if (loading) return (
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
            <div className="min-h-screen animate-pulse rounded-sm" style={{ backgroundColor: 'var(--line)' }} />
        </div>
    );

    if (!product) return null;

    // Las hasta 3 fotos del mismo producto: la principal (image_url) más las
    // adicionales (photos). Se filtran vacíos porque photos puede traer un
    // hueco si solo se cargó la foto 2 y no la 3.
    const photoList = [product.image_url, ...(product.photos || [])].filter(Boolean);

    // La variante elegida manda sobre la foto elegida; si no hay ninguna de
    // las dos, la principal. `productImage` devuelve la versión fundida al
    // tono papel cuando esa foto la tiene.
    const heroImage = selectedVariantImg
        ? productImage(selectedVariantImg)
        : productImage(photoList[selectedPhotoIdx] || product.image_url);

    // Único producto que no es papel por hoja: se vende por kilo, no por
    // paca, así que el copy y las specs pensadas para papel no le calzan.
    const isBolsas = product.category === 'bolsas_poliseda';

    // El mismo `matches()` que clasifica las páginas de sector dice a qué
    // página de sector enlazar desde la ficha, y también decide el
    // cross-sell: 10 de 13 productos comparten category="papel" (verificado
    // contra el catálogo real), así que filtrar solo por categoría mezclaría
    // papel cebolla de 17g con papel revolución de 49g como "relacionado".
    // El sector clasifica por GRAMAJE real, que es la razón por la que dos
    // papeles sirven al mismo negocio — mejor señal que la categoría cruda,
    // y sin lista curada a mano: un producto nuevo entra solo.
    const matchingSector = SECTORS.find((s) => s.matches(product));
    // Guías que responden las dudas previas a comprar ESTE papel. Ver
    // lib/relatedGuides.js: la ficha no enlazaba a ninguna.
    const relatedGuides = guiasParaProducto(product);
    // Preguntas frecuentes de ESTA ficha, a partir de sus datos reales. La
    // misma lista alimenta el bloque visible y el FAQPage schema.
    const faq = productFaq(product, matchingSector);
    const medidas = medidasTexto(product);
    // La bolsa de asas no está en la categoría 'bolsas_poliseda' (su categoría
    // es texto libre) pero es poliseda: decir "papel grado alimenticio" de una
    // bolsa de plástico sería un dato falso en el título.
    const esPoliseda = isBolsas || /poliseda/i.test(product.category || '');
    const gramajeEnNombre = /\d\s*(g|gr)\b/i.test(product.name);
    // Título: nombre + gramaje + giro del producto, que es como se busca
    // ("papel oro 29 g"). Si no cabe en ~62 caracteres (Google corta ahí), se
    // pasa a la forma corta en vez de dejar que el buscador lo recorte a
    // media frase.
    const gramajeTitulo = product.weight_grams > 0 && !esPoliseda && !gramajeEnNombre ? ` ${product.weight_grams} g` : '';
    const tipoTitulo = esPoliseda ? 'Bolsas de Poliseda' : 'Papel Grado Alimenticio';
    const tituloSeo = [
        `${product.name}${gramajeTitulo} | ${tipoTitulo} | Carpepel`,
        `${product.name}${gramajeTitulo} | Carpepel`,
        `${product.name} | Carpepel`,
    ].find((t) => t.length <= 62) || `${product.name} | Carpepel`;
    // Descripción: datos concretos por delante (lo que responde la búsqueda),
    // no el texto genérico del admin, que además se repetía casi igual entre
    // productos. Todo sale del catálogo; nada se inventa.
    const factual = (() => {
        const precio = `$${product.price} MXN`;
        const cierre = ' Mayoreo y menudeo, envíos a toda la República.';
        if (isBolsas) {
            const m = medidas ? `, medidas ${medidas.texto}` : '';
            const largo = `${product.name}: bolsas de poliseda vendidas por kilogramo${m}. ${precio}/kg.${cierre}`;
            // Google corta cerca de 160 caracteres: con tres medidas largas se
            // pasaba, así que en ese caso se omiten las medidas (siguen en la
            // página y en el schema).
            return largo.length <= 158
                ? largo
                : `${product.name}: bolsas de poliseda vendidas por kilogramo. ${precio}/kg.${cierre}`;
        }
        if (product.weight_grams > 0 && !esPoliseda) {
            const m = medidas ? `, ${medidas.plural ? 'medidas' : 'medida'} ${medidas.texto}` : '';
            const paca = product.package_weight_kg > 0 ? `, paca de ${product.package_weight_kg} kg` : '';
            const largo = `${product.name}: papel grado alimenticio de ${product.weight_grams} g${m}${paca}. ${precio}.${cierre}`;
            return largo.length <= 158
                ? largo
                : `${product.name}: papel grado alimenticio de ${product.weight_grams} g${paca}. ${precio}.${cierre}`;
        }
        // Sin gramaje real (hay productos con 0): nunca se imprime "0 g".
        return metaDescription(product.description, ` Precio: ${precio}. Envíos a toda la República Mexicana.`);
    })();

    const relatedProducts = allProducts
        .filter((p) => p.id !== product.id && (matchingSector ? matchingSector.matches(p) : p.category === product.category))
        .slice(0, 4);

    return (
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-8" data-testid="product-detail-page">
            <Seo
                title={tituloSeo}
                description={factual}
                canonicalPath={`/producto/${product.id}`}
                image={product.image_url}
                schema={[
                    {
                        "@type": "Product",
                        name: product.name,
                        description: product.description,
                        // Todas las fotos reales del producto, no solo la
                        // principal: Google usa el resto en el carrusel de
                        // imágenes del resultado enriquecido.
                        image: photoList.length ? photoList : product.image_url,
                        sku: product.id,
                        category: isBolsas ? "Bolsas de poliseda grado alimenticio" : "Papel grado alimenticio",
                        itemCondition: "https://schema.org/NewCondition",
                        // Datos técnicos que el propio catálogo ya publica.
                        // Solo se declara lo que existe: un producto sin
                        // gramaje o sin medida registrada no inventa ninguno.
                        ...(product.package_weight_kg
                            ? { weight: { "@type": "QuantitativeValue", value: product.package_weight_kg, unitCode: "KGM" } }
                            : {}),
                        ...(medidas
                            ? { size: medidas.texto }
                            : {}),
                        ...(product.weight_grams
                            ? { additionalProperty: [{
                                "@type": "PropertyValue",
                                name: "Gramaje",
                                value: product.weight_grams,
                                // g/m², no "GRM": ese código UN/CEFACT es gramo
                                // (peso), y el gramaje de un papel es densidad
                                // superficial. Con unitText no se afirma una
                                // unidad equivocada.
                                unitText: "g/m²",
                            }] }
                            : {}),
                        offers: {
                            "@type": "Offer",
                            url: `https://www.carpepel.com/producto/${product.id}`,
                            priceCurrency: "MXN",
                            price: product.price,
                            // Antes decía SIEMPRE "InStock", incluso con
                            // existencia 0 — declarar disponible algo agotado
                            // es justo el tipo de desajuste entre schema y
                            // realidad por el que Google aplica acciones
                            // manuales, y de paso frustra a quien llega.
                            availability: product.stock > 0
                                ? "https://schema.org/InStock"
                                : "https://schema.org/OutOfStock",
                            itemCondition: "https://schema.org/NewCondition",
                            // Mismo @id que declara la portada: le dice al
                            // buscador que el vendedor de este producto y el
                            // negocio de la ficha local son la misma entidad.
                            seller: { "@id": "https://www.carpepel.com/#carpepel" },
                            // Precio real por kilogramo, no por pieza: sin esto el
                            // rich result mostraría "$70" como si fuera el total.
                            ...(isBolsas ? { priceSpecification: {
                                "@type": "UnitPriceSpecification",
                                price: product.price,
                                priceCurrency: "MXN",
                                unitCode: "KGM",
                            } } : {}),
                        },
                    },
                    {
                        "@type": "BreadcrumbList",
                        itemListElement: [
                            { "@type": "ListItem", position: 1, name: "Inicio", item: "https://www.carpepel.com/" },
                            { "@type": "ListItem", position: 2, name: "Catálogo", item: "https://www.carpepel.com/catalogo" },
                            { "@type": "ListItem", position: 3, name: product.name, item: `https://www.carpepel.com/producto/${product.id}` },
                        ],
                    },
                    // Mismo arreglo que se pinta abajo: schema y texto visible
                    // no pueden divergir.
                    faqSchema(faq),
                ]}
            />
            {/* Miga de pan visible — el schema BreadcrumbList de arriba describe
                EXACTAMENTE esta ruta, nunca al revés: schema que no coincide con
                lo que el usuario ve es motivo de acción manual de Google. */}
            <nav aria-label="Miga de pan" className="mb-6">
                <ol className="flex items-center gap-2 flex-wrap font-mono text-[11px] uppercase tracking-[0.1em]">
                    <li><Link to="/" className="transition-opacity hover:opacity-70" style={{ color: 'var(--ink-soft)' }}>Inicio</Link></li>
                    <li aria-hidden="true" style={{ color: 'var(--line)' }}>/</li>
                    <li><Link to="/catalogo" className="transition-opacity hover:opacity-70" style={{ color: 'var(--ink-soft)' }}>Catálogo</Link></li>
                    <li aria-hidden="true" style={{ color: 'var(--line)' }}>/</li>
                    <li aria-current="page" style={{ color: 'var(--ink)' }} className="truncate max-w-[50vw]">{product.name}</li>
                </ol>
            </nav>
            <button
                data-testid="product-back-btn"
                onClick={() => navigate(-1)}
                className="flex items-center gap-2 text-sm mb-8 transition-opacity duration-200 hover:opacity-70"
                style={{ color: 'var(--ink-soft)' }}
            >
                <ArrowLeft className="w-4 h-4" /> Volver
            </button>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                {/* Galería — mismo tratamiento que el hero: lienzo en tono papel
                    y `object-contain`. Las fotos tratadas se funden con el
                    lienzo y el pliego flota; las de ambiente se leen como la
                    fotografía que son, dentro del mismo marco sobrio. */}
                <div>
                    {/* La foto tratada no lleva marco: su fondo ES el de la página,
                        así que el pliego flota igual que en el hero. Las de
                        ambiente sí lo llevan — sin él quedarían como un recorte
                        rectangular suelto en medio del papel. */}
                    <div
                        className={`relative rounded-sm overflow-hidden ${heroImage.floats ? '' : 'border'}`}
                        style={{
                            backgroundColor: 'var(--paper)',
                            borderColor: heroImage.floats ? undefined : 'var(--line)',
                            // Mismo nombre que la miniatura de ProductCard: el
                            // navegador hace un morph directo entre catálogo y
                            // ficha en vez de un cross-fade genérico de página.
                            viewTransitionName: `product-photo-${product.id}`,
                        }}
                        data-testid="product-image-area"
                    >
                        {heroImage.src ? (
                            <ProgressiveImage
                                src={heroImage.src}
                                alt={product.name}
                                imgClassName="w-full aspect-[4/3] object-contain"
                                loading="eager"
                                sizes="(min-width: 1024px) 640px, 100vw"
                                /* Sin el fundido: es el elemento LCP de esta
                                   página, y el efecto la mantenía invisible
                                   hasta que hidratara el bundle (53% del LCP
                                   era render delay, medido). */
                                priority
                                fetchpriority="high"
                            />
                        ) : (
                            <div className="aspect-[4/3] flex items-center justify-center">
                                <div className="text-center">
                                    <Package className="w-16 h-16 mx-auto mb-4" style={{ color: 'var(--kraft)' }} />
                                    <p className="font-mono text-[11px] uppercase tracking-[0.18em]" style={{ color: 'var(--ink-soft)' }}>
                                        Papel grado alimenticio
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Fotos del mismo producto (otro ángulo/toma) — distinto del
                        selector de diseños de abajo, que cambia de producto. */}
                    {photoList.length > 1 && (
                        <div className="mt-4" data-testid="product-photo-thumbs">
                            <p className="font-mono text-[10px] uppercase tracking-[0.18em] mb-3" style={{ color: 'var(--ink-soft)' }}>
                                Más fotos
                            </p>
                            <div className="grid grid-cols-5 gap-2">
                                {photoList.map((img, i) => {
                                    const isActive = !selectedVariantImg && selectedPhotoIdx === i;
                                    return (
                                        <button
                                            key={i}
                                            type="button"
                                            data-testid={`photo-thumb-${i}`}
                                            aria-pressed={isActive}
                                            aria-label={i === 0 ? 'Foto principal' : `Foto ${i + 1}`}
                                            onClick={() => { setSelectedPhotoIdx(i); setSelectedVariantImg(null); }}
                                            className="rounded-sm overflow-hidden border-2 transition-transform duration-200 aspect-square hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                                            style={{
                                                borderColor: isActive ? 'var(--ink)' : 'var(--line)',
                                                backgroundColor: 'var(--paper)',
                                            }}
                                        >
                                            {/* object-cover: mismo marco cuadrado para las 3 fotos
                                                sin importar la proporción con la que se subieron. */}
                                            <img src={resolveSrc(img)} alt="" aria-hidden="true" className="w-full h-full object-cover" loading="lazy" />
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {product.gallery_images?.length > 1 && (
                        <div className="mt-4">
                            <p className="font-mono text-[10px] uppercase tracking-[0.18em] mb-3" style={{ color: 'var(--ink-soft)' }}>
                                Elige tu diseño
                            </p>
                            <div className="grid grid-cols-5 gap-2">
                                {product.gallery_images.map((img, i) => {
                                    const isActive = (selectedVariantImg || product.image_url) === img;
                                    return (
                                        <button
                                            key={i}
                                            type="button"
                                            data-testid={`variant-btn-${i}`}
                                            aria-pressed={isActive}
                                            aria-label={`Diseño ${i + 1}`}
                                            onClick={() => setSelectedVariantImg(img)}
                                            className="rounded-sm overflow-hidden border-2 transition-transform duration-200 aspect-square hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                                            style={{
                                                borderColor: isActive ? 'var(--ink)' : 'var(--line)',
                                                backgroundColor: 'var(--paper)',
                                            }}
                                        >
                                            <img src={resolveSrc(img)} alt="" aria-hidden="true" className="w-full h-full object-cover" loading="lazy" />
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>

                {/* Product Info */}
                <div>
                    {/* Rótulo derivado, no el campo `category` crudo: en el catálogo
                        real ese campo se usó como texto libre ("Envío gratis papel
                        sanck", "2,400 hojas medida de 1 kilo…"), y mostrarlo
                        prometía envío gratis donde el envío se cobra aparte. */}
                    <p className="eyebrow mb-4">
                        {isBolsas || esPoliseda ? 'Bolsas de Poliseda' : product.category === 'papel_snacks' ? 'Papel para snacks' : 'Papel grado alimenticio'}
                    </p>

                    <h1
                        className="font-heading text-4xl sm:text-5xl font-black mb-4 text-balance"
                        style={{ color: 'var(--ink)' }}
                        data-testid="product-name"
                    >
                        {product.name}
                    </h1>

                    <p
                        className="text-[15px] leading-relaxed mb-7 text-pretty"
                        style={{ color: 'var(--ink-soft)' }}
                        data-testid="product-description"
                    >
                        {product.description}
                    </p>

                    {/* Specs — reglas verticales en vez de caja gris: mismo ritmo
                        editorial que el resto del sitio. */}
                    <dl
                        className="grid grid-cols-3 gap-4 mb-7 py-5 border-t border-b"
                        style={{ borderTopColor: 'var(--ink)', borderBottomColor: 'var(--line)' }}
                        data-testid="product-specs"
                    >
                        {(isBolsas
                            ? [
                                [Ruler, product.sizes?.length, product.sizes?.length === 1 ? 'Medida' : 'Medidas'],
                                [Package, '100% amigable', 'Con el ambiente'],
                                [Weight, `$${product.price}/kg`, 'Precio'],
                            ]
                            : [
                                [Weight, `${product.weight_grams}g`, 'Gramaje'],
                                [Ruler, product.sizes?.length, product.sizes?.length === 1 ? 'Medida' : 'Medidas'],
                                [Package, `${product.package_weight_kg}kg`, 'Paquete'],
                            ]
                        ).map(([Icon, value, label], i) => (
                            <div
                                key={label}
                                className={`text-center ${i === 1 ? 'border-x' : ''}`}
                                style={i === 1 ? { borderColor: 'var(--line)' } : undefined}
                            >
                                <Icon className="w-5 h-5 mx-auto mb-1.5" strokeWidth={1.5} style={{ color: 'var(--kraft-deep)' }} />
                                <dd className="font-heading text-lg font-bold" style={{ color: 'var(--ink)' }}>{value}</dd>
                                <dt className="font-mono text-[9px] uppercase tracking-[0.14em] mt-0.5" style={{ color: 'var(--ink-soft)' }}>
                                    {label}
                                </dt>
                            </div>
                        ))}
                    </dl>

                    {/* Size Selection */}
                    {product.sizes?.length > 0 && (
                        <div className="mb-6">
                            <p className="font-mono text-[10px] uppercase tracking-[0.18em] mb-3" style={{ color: 'var(--ink-soft)' }}>
                                Medida (cm)
                            </p>
                            <div className="flex flex-wrap gap-2">
                                {product.sizes.map(size => {
                                    const on = selectedSize === size;
                                    return (
                                        <button
                                            key={size}
                                            type="button"
                                            aria-pressed={on}
                                            data-testid={`size-btn-${size}`}
                                            onClick={() => setSelectedSize(size)}
                                            className="px-4 py-2 border-2 rounded-sm text-sm font-bold uppercase tracking-wider transition-colors duration-200"
                                            style={{
                                                borderColor: on ? 'var(--ink)' : 'var(--line)',
                                                backgroundColor: on ? 'var(--ink)' : 'transparent',
                                                color: on ? 'var(--paper)' : 'var(--ink-soft)',
                                            }}
                                        >
                                            {size}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Quantity */}
                    <div className="mb-8">
                        <p className="font-mono text-[10px] uppercase tracking-[0.18em] mb-3" style={{ color: 'var(--ink-soft)' }}>
                            {isBolsas ? 'Cantidad (kg)' : 'Cantidad (paquetes)'}
                        </p>
                        <div className="flex items-center gap-3">
                            <button
                                type="button"
                                aria-label={isBolsas ? 'Quitar un kilogramo' : 'Quitar un paquete'}
                                data-testid="quantity-decrease"
                                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                                className="w-10 h-10 border-2 rounded-sm flex items-center justify-center transition-colors duration-200"
                                style={{ borderColor: 'var(--line)', color: 'var(--ink)' }}
                            >
                                <Minus className="w-4 h-4" />
                            </button>
                            <span className="font-heading text-xl font-bold w-12 text-center tabular-nums" style={{ color: 'var(--ink)' }} data-testid="quantity-display">
                                {quantity}
                            </span>
                            <button
                                type="button"
                                aria-label={isBolsas ? 'Agregar un kilogramo' : 'Agregar un paquete'}
                                data-testid="quantity-increase"
                                onClick={() => setQuantity(quantity + 1)}
                                className="w-10 h-10 border-2 rounded-sm flex items-center justify-center transition-colors duration-200"
                                style={{ borderColor: 'var(--line)', color: 'var(--ink)' }}
                            >
                                <Plus className="w-4 h-4" />
                            </button>
                        </div>
                    </div>

                    {/* Price + Add to Cart */}
                    <div
                        ref={setBuyEl}
                        className="flex items-center justify-between gap-4 p-6 border-2 rounded-sm"
                        style={{ borderColor: 'var(--ink)', backgroundColor: 'var(--paper-2)' }}
                    >
                        <div>
                            <p className="font-mono text-[10px] uppercase tracking-[0.18em]" style={{ color: 'var(--ink-soft)' }}>
                                Total
                            </p>
                            <p className="font-heading text-3xl font-black" style={{ color: 'var(--ink)' }} data-testid="product-total">
                                ${(product.price * quantity).toLocaleString()}
                                <span className="font-mono text-xs font-normal ml-1.5" style={{ color: 'var(--ink-soft)' }}>MXN</span>
                            </p>
                        </div>
                        <Button
                            ref={addToCartRef}
                            data-testid="add-to-cart-btn"
                            onClick={handleAddToCart}
                            disabled={adding}
                            className="magnetic h-12 px-8 rounded-sm uppercase font-bold tracking-widest text-sm hover:opacity-85 disabled:opacity-60 whitespace-nowrap"
                            style={
                                added
                                    ? { backgroundColor: 'var(--success)', color: 'var(--paper)' }
                                    : { backgroundColor: 'var(--ink)', color: 'var(--paper)' }
                            }
                        >
                            {added
                                ? <><Check className="w-4 h-4 mr-2" /> Agregado</>
                                : <><ShoppingCart className="w-4 h-4 mr-2" /> {adding ? 'Agregando…' : 'Agregar'}</>}
                        </Button>
                    </div>
                    {/* Justo bajo el botón, donde aparece la duda: la respuesta
                        sin otro clic. Mismo texto que el hero y la portada. */}
                    <p className="mt-3 flex flex-wrap gap-x-4 gap-y-1 font-mono text-[10px] uppercase tracking-[0.14em]" style={{ color: 'var(--ink-soft)' }} data-testid="product-trust">
                        {['Sin crear cuenta', 'Pago seguro', 'Envío a todo México'].map((t) => (
                            <span key={t} className="flex items-center gap-1.5">
                                <Check className="w-3 h-3" strokeWidth={2} style={{ color: 'var(--success)' }} />{t}
                            </span>
                        ))}
                    </p>
                    <AcceptedLogos className="h-5 mt-3" />

                    {matchingSector && (
                        <Link
                            to={`/papel-para/${matchingSector.slug}`}
                            data-testid="product-sector-link"
                            className="mt-4 flex items-center gap-1.5 text-xs font-medium transition-opacity hover:opacity-70"
                            style={{ color: 'var(--ink-soft)' }}
                        >
                            Ver todo el papel para {matchingSector.label.toLowerCase()} <ArrowRight className="w-3 h-3" />
                        </Link>
                    )}
                </div>
            </div>

            {/* Cross-sell: mismo motivo por el que existe en cualquier tienda
                real — sube el ticket promedio y, a la vez, es enlazado
                interno real entre fichas de producto (antes no existía
                ninguno: la única salida de esta página era "volver al
                catálogo"). */}
            {/* Lo que va debajo de la ficha se numera como la portada: 01, 02, 03. */}
            <div className="folio-sections">
            <div className="mt-16 pt-10 border-t" style={{ borderColor: 'var(--line)' }}>
                <FaqSection
                    eyebrow="Antes de comprar"
                    heading={`Preguntas frecuentes sobre ${product.name}`}
                    items={faq}
                    testId="product-faq"
                />
            </div>

            {relatedGuides.length > 0 && (
                <div className="mt-16 pt-10 border-t" style={{ borderColor: 'var(--line)' }} data-testid="related-guides">
                    <p className="eyebrow mb-5">Antes de comprar</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {relatedGuides.map((g) => (
                            <Link
                                key={g.slug}
                                to={`/guias/${g.slug}`}
                                className="group block p-5 border rounded-sm transition-colors duration-200"
                                style={{ borderColor: 'var(--line)', backgroundColor: 'var(--paper-2)' }}
                            >
                                <span className="block font-heading font-bold text-base leading-snug" style={{ color: 'var(--ink)' }}>
                                    {g.title}
                                </span>
                                <span className="block text-xs leading-relaxed mt-2" style={{ color: 'var(--ink-soft)' }}>
                                    {g.excerpt}
                                </span>
                                <span className="inline-flex items-center gap-1.5 mt-3 font-mono text-[10px] uppercase tracking-[0.16em]" style={{ color: 'var(--kraft-deep)' }}>
                                    Leer la guía
                                    <ArrowRight className="w-3 h-3 transition-transform duration-300 group-hover:translate-x-0.5" />
                                </span>
                            </Link>
                        ))}
                    </div>
                </div>
            )}

            {relatedProducts.length > 0 && (
                <div className="mt-16 pt-10 border-t" style={{ borderColor: 'var(--line)' }} data-testid="related-products">
                    <p className="eyebrow mb-5">También te puede interesar</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {relatedProducts.map((p, i) => (
                            <ProductCard key={p.id} product={p} index={i} />
                        ))}
                    </div>
                </div>
            )}
            </div>

            {/* Barra de compra fija: solo celular y solo cuando el bloque de
                precio de la ficha está fuera de pantalla. Muestra la medida
                elegida para que nadie agregue un tamaño sin saberlo. */}
            <div
                className="md:hidden fixed inset-x-0 bottom-0 z-40 border-t-2 px-4 pt-3 flex items-center justify-between gap-3 transition-transform duration-300 ease-out"
                style={{
                    borderColor: 'var(--ink)',
                    backgroundColor: 'var(--paper)',
                    paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))',
                    transform: stickyBuy ? 'translateY(0)' : 'translateY(110%)',
                    boxShadow: '0 -8px 24px rgba(28,26,21,0.08)',
                }}
                aria-hidden={!stickyBuy}
                data-testid="sticky-buy-bar"
            >
                <div className="min-w-0">
                    <p className="font-mono text-[10px] uppercase tracking-[0.14em] truncate" style={{ color: 'var(--ink-soft)' }}>
                        {quantity} {isBolsas ? 'kg' : (quantity === 1 ? 'paquete' : 'paquetes')}{selectedSize ? ` · ${selectedSize}` : ''}
                    </p>
                    <p className="font-heading text-2xl font-black leading-tight" style={{ color: 'var(--ink)' }}>
                        ${(product.price * quantity).toLocaleString()}
                        <span className="font-mono text-[10px] font-normal ml-1" style={{ color: 'var(--ink-soft)' }}>MXN</span>
                    </p>
                </div>
                <Button
                    data-testid="sticky-add-to-cart-btn"
                    onClick={handleAddToCart}
                    disabled={adding}
                    tabIndex={stickyBuy ? 0 : -1}
                    className="h-12 px-7 rounded-sm uppercase font-bold tracking-widest text-sm hover:opacity-85 disabled:opacity-60 whitespace-nowrap"
                    style={added ? { backgroundColor: 'var(--success)', color: 'var(--paper)' } : { backgroundColor: 'var(--ink)', color: 'var(--paper)' }}
                >
                    {added
                        ? <><Check className="w-4 h-4 mr-2" /> Agregado</>
                        : <><ShoppingCart className="w-4 h-4 mr-2" /> {adding ? 'Agregando…' : 'Agregar'}</>}
                </Button>
            </div>
            {/* Reserva el alto de la barra para que no tape el pie de página. */}
            <div className="md:hidden h-20" aria-hidden="true" />
        </div>
    );
}
